/* eslint-disable */
import { useFormik } from 'formik';
import { debounce } from 'lodash';
import filter from 'lodash/filter';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import moment from 'moment';
import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import calcLicenses from './common/calcLicense';

import apiConfiguration from '~/api/configuration';
import apiLicenseStatus from '~/api/licenseStatus';
import apiPipeline from '~/api/pipeline';
import api from '~/api/pipelines';
import apiRuntime from '~/api/runtime';
import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import withHooks from '~/common/hoc/withHooks';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';

const PipelineSchema = yup.object().shape({
  isReplicas: yup.boolean(),
  isPipelineEngine: yup.boolean(),
  pipeline: yup.object().shape({
    value: yup.string().required('common.messages.error.required_field'),
    label: yup.string().required('common.messages.error.required_field')
  }),
  version: yup.object().shape({
    value: yup.string().required('common.messages.error.required_field'),
    label: yup.string().required('common.messages.error.required_field')
  }),
  replicasDeploy: yup.object().when('isReplicas', (isReplicas, schema) => {
    if (isReplicas) {
      return schema.shape({
        value: yup.string().required('common.messages.error.required_field'),
        label: yup.string().required('common.messages.error.required_field')
      });
    }
    return schema.shape({
      value: yup.string(),
      label: yup.string()
    });
  }),
  configuration: yup.string().required('common.messages.error.required_field')
});

const createObjectPipelineVersion = pipeline => ({
  value: pipeline.id,
  label: `${pipeline?.versionMajor}.${pipeline?.versionMinor}`
});

const createObjectOption = deployment => {
  if (deployment?.replicaName) {
    const removeNameMultiInstanceThePipeline =
      deployment?.pipeline?.name?.replace?.(`-${deployment?.replicaName}`, '');
    return {
      label: `${removeNameMultiInstanceThePipeline} - V${deployment?.pipeline?.versionMajor}`,
      value: deployment?.pipeline.id
    };
  }
  return {
    label: `${deployment?.pipeline?.name} - V${deployment?.pipeline?.versionMajor}`,
    value: deployment?.pipeline.id
  };
};
const normalizePipelines = pipelines => {
  const data = map(pipelines, pipeline => ({
    label: `${pipeline.name} - V${pipeline.versionMajor}`,
    value: pipeline?._id
  }));
  return data;
};
const normalizeSize = name => {
  if (!name) return '';
  return name?.split('-')[0];
};

const normalizeNamePipeline = (name, multiInstanceName) =>
  multiInstanceName ? `${name}-${multiInstanceName}` : name;

const createObjectViewDeploy = data => {
  const name = normalizeNamePipeline(
    data?.pipeline.name,
    data?.parameterizedReplica
  );
  return {
    ...data,
    pipeline: {
      ...data?.pipeline,
      name
    },
    activeConfiguration: {
      ...data.activeConfiguration,
      actualConsumers: data.consumers
    },
    licenses: calcLicenses(data?.activeConfiguration?.name, data?.replicas),
    availableReplicas: `0/0`,
    errorCount: 0,
    oomCount: 0,
    replicas: data?.replicas,
    status: { text: 'STARTING' }
  };
};

const enhancer = withHooks(props => {
  const [projectId, setProjectId] = useState(null);
  const [configurations, setConfigurations] = useState(null);
  const [pipelineInstances, setPipelineInstences] = useState(null);
  const [validationLicense, setValidationLicense] = useState([]);
  const [validationPipeline, setValidationPipeline] = useState([]);
  const [configurationLastDeploy, setConfigurationLastDeploy] = useState(null);
  const [sliderProps, setSliderProps] = useState({ max: 0 });
  const [openPromote, setOpenPromote] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [newDeploy, setNewDeploy] = useState();
  const [loadingPromote, setLoadingPromote] = useState(false);
  const deploymentSelect = props.showPromote ? props.deploymentSelected : false;
  const deployment =
    deploymentSelect ||
    useSelector(state => state?.runtime?.deployment, isEqual);
  const [newDeployment, setNewDeployment] = useState(deployment);
  const [pipeline, setPipeline] = useState(deploymentSelect?.pipeline);

  const pipelineRedeploy = useSelector(
    state => state?.runtime?.pipeline,
    isEqual
  );
  const dispatch = useDispatch();

  const { RUN_CHANGE_FETCHAPI, RUN_ROUTES_PARAMS } = featureFlagConstants;
  const { treatments } = useFeatureFlag(
    [RUN_CHANGE_FETCHAPI, RUN_ROUTES_PARAMS],
    {
      realm: props?.realm
    }
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const isNewRoutesParams = treatments[RUN_ROUTES_PARAMS].treatment === 'on';

  const isNewFetchPipeline = treatments[RUN_CHANGE_FETCHAPI].treatment === 'on';

  const texts = {
    deploy: {
      title: 'scenes.runtime.labels.deploy_pipeline',
      button: 'common.actions.deploy',
      modalConfirmation: {
        title: 'label.deploy_pipeline_prompt',
        text: 'label.deploy_pipeline_msg',
        confirmText: 'common.actions.deploy'
      },
      datatestidText: 'run-confirm-deploy'
    },
    redeploy: {
      title: 'label.redeploy_pipeline',
      button: 'action.redeploy',
      modalConfirmation: {
        title: 'label.redeploy_pipeline_prompt',
        text: 'label.redeploy_pipeline_msg',
        confirmText: 'action.redeploy'
      },
      datatestidText: 'run-confirm-redeploy'
    },
    promote: {
      title: 'label.pipeline_promotion_configuration',
      button: 'action.promote',
      datatestidText: 'run-confirm-promote'
    }
  };

  const deployType = props.showPromote
    ? 'promote'
    : pipelineRedeploy
    ? 'redeploy'
    : 'deploy';

  const form = useFormik({
    initialValues: {
      isPipelineEngine: false,
      isReplicas: false,
      pipeline: { value: '', label: '' },
      version: { value: '', label: '' },
      replicasDeploy: { value: '', label: '' },
      projectName: '',
      configuration: null,
      replicas: 1,
      consumer: 1
    },
    validationSchema: PipelineSchema,
    onSubmit: async values => {
      const config = find(
        configurations,
        item => normalizeSize(item.name) === values?.configuration
      );
      const size = normalizeSize(config?.name);

      const environment = props.showPromote
        ? values.environment?.value
        : props.environment;

      const newDeploy = {
        configuration: {
          id: config?.id,
          size,
          consumers: values?.consumer
        },
        pipelineId: values?.version?.value,
        force: !!configurationLastDeploy,
        replicas: values?.replicas,
        environment,
        realm: props?.realm,
        replicaInstanceName: values?.replicasDeploy?.value,
        type: deployType,
        isPipelineEngine: values?.isPipelineEngine
      };
      const pipelineLastVersion = find(
        pipeline?.versionMinor,
        item => item.id === values?.version?.value
      );

      const licenseStatus = await apiLicenseStatus.fetch({
        environment,
        realm: props.realm,
        replicas: values?.replicas,
        pipelineId: values?.version?.value,
        size
      });

      const objViewDeploy = createObjectViewDeploy({
        pipeline: {
          ...deployment?.pipeline,
          ...pipelineLastVersion,
          triggerSpec: {
            ...pipelineLastVersion,
            ...pipeline.triggerSpec
          }
        },
        activeConfiguration: {
          ...config,
          replicaCount: values?.replicas
        },
        deploymentStatus: deployment?.deploymentStatus,
        licenses: pipeline?.licenses,
        replicas: values?.replicas,
        startTime: moment().valueOf(),
        force: !!configurationLastDeploy,
        licenseTotal: pipeline?.licenseTotal,
        parameterizedReplica: values?.replicasDeploy?.value,
        consumers: values?.consumer,
        licenseStatus
      });

      if (props.showPromote) {
        setNewDeployment({
          deployment: {
            ...objViewDeploy,
            newEnvironment: environment
          }
        });
        setOpenPromote(true);
      } else {
        setNewDeploy({
          ...newDeploy,
          environment,
          deployment: objViewDeploy,
          texts: texts[deployType].modalConfirmation,
          isRedeploy: pipelineRedeploy,
          projectId,
          projectName: values?.projectName,
          datatestidText: 'button-confirm-redeploy'
        });
        setShowConfirmDialog(true);
      }
    }
  });

  const setConfigAndReplicas = async ({
    pipelineName,
    versionMajor,
    replicaName
  }) => {
    try {
      const { data } = await apiRuntime.findByNameAndVersionMajor({
        realm: props.realm,
        environment: props.environment,
        pipelineName: normalizeNamePipeline(pipelineName, replicaName),
        versionMajor
      });
      setConfigurationLastDeploy(data?.deploymentByNameAndVersionMajor);
      if (data?.deploymentByNameAndVersionMajor) {
        setSliderProps({
          max: data?.deploymentByNameAndVersionMajor?.activeConfiguration
            ?.consumers
        });
        form.setFieldValue(
          'replicas',
          data?.deploymentByNameAndVersionMajor?.activeConfiguration
            ?.replicaCount
        );
        form.setFieldValue(
          'configuration',
          normalizeSize(
            data?.deploymentByNameAndVersionMajor?.activeConfiguration?.name
          )
        );
        form.setFieldValue(
          'consumer',
          data?.deploymentByNameAndVersionMajor?.activeConfiguration
            ?.actualConsumers
        );
      }
    } catch (e) {
      setConfigurationLastDeploy(null);
    }
  };

  const addConfigurations = async ({ pipelineVersionId }) => {
    setConfigurations(null);
    try {
      const { data } = await apiConfiguration.find({
        pipelineVersionId,
        realm: props.realm,
        environment: props.environment
      });
      setConfigurations(data?.configurations);
    } catch (e) {
      setConfigurations(null);
    }
  };

  const checkLicense = async variant => {
    if (variant?.size) {
      const size = normalizeSize(variant?.size);
      try {
        const { data } = await apiRuntime.checkLicense({
          ...variant,
          size,
          realm: props.realm,
          environment: props.environment
        });
        setValidationLicense(data?.checkLicense?.analysis);
      } catch (e) {
        setValidationLicense([]);
      }
    }
  };

  const validatePipeline = async variant => {
    try {
      const data = await apiPipeline.validatePipeline({
        ...variant,
        realm: props.realm
      });

      setValidationPipeline(data?.analysis);
    } catch (e) {
      setValidationPipeline([]);
    }
  };

  const setMultiInstances = async variant => {
    try {
      if (variant?.replicaName) {
        const { data } = await apiPipeline.multiInstance({
          ...variant,
          realm: props.realm,
          environment: props.environment
        });
        setPipelineInstences(data?.replicaInstance);
      }
    } catch (e) {
      setPipelineInstences([]);
    }
  };

  const closePromote = () => {
    setOpenPromote(false);
  };

  const pipelineToPromoteConfirm = async deploymentData => {
    setLoadingPromote(true);
    const newPayload = {
      configuration: {
        consumers: deploymentData.activeConfiguration.actualConsumers,
        id: deploymentData.activeConfiguration.id,
        size: normalizeSize(deploymentData.activeConfiguration.name)
      },
      environment: deploymentData.newEnvironment,
      force: true,
      pipelineId: deploymentData.pipeline.id,
      replicaInstanceName: deploymentData.replicaName,
      realm: props.realm,
      replicas: deploymentData.replicas
    };

    if (form.values.deletePipe) {
      props.remove({
        environment: deployment?.activeConfiguration?.environment?.name,
        realm: props.realm,
        deployment: deployment.id
      });
    }

    props.createDeploy(newPayload);

    const { project } = await api.pipelineProjectName({
      id: deployment.pipeline.id,
      realm: props?.realm
    });

    props.changeEnvironment(deploymentData.newEnvironment);
    setOpenPromote(false);
    setLoadingPromote(false);
    props.close();

    if (isNewRoutesParams) {
      setSearchParams({
        pipelineName: searchParams.get('pipelineName'),
        projectId: project.id
      });
    }
  };
  const actions = {
    loadPipelines: async (search, _, { searchPage, page, previousSearch }) => {
      if (isNewFetchPipeline) {
        return new Promise((resolve, reject) =>
          debounce(async () => {
            try {
              const request = await api.fetch({
                realm: props?.realm,
                size: 10,
                search: {
                  name: search,
                  page: search
                    ? previousSearch == search
                      ? ++searchPage
                      : 0
                    : ++page
                }
              });
              const options = normalizePipelines(
                request?.data?.searchPipelines?.content
              );

              const hasMore = !request?.data?.searchPipelines?.last;

              resolve({
                options,
                hasMore,
                additional: {
                  page: !search ? request?.data?.searchPipelines?.number : page,
                  searchPage: search
                    ? previousSearch == search
                      ? request?.data?.searchPipelines?.number
                      : 0
                    : -1,
                  previousSearch: search
                }
              });
            } catch (error) {
              reject();
            }
          }, 1000)()
        );
      }

      try {
        const request = await api.fetch({
          realm: props?.realm,
          size: 10,
          search: {
            name: search,
            page: search
              ? previousSearch == search
                ? ++searchPage
                : 0
              : ++page
          }
        });

        const options = normalizePipelines(
          request?.data?.searchPipelines?.content
        );

        const hasMore = !request?.data?.searchPipelines?.last;

        return {
          options,
          hasMore,
          additional: {
            page: !search ? request?.data?.searchPipelines?.number : page,
            searchPage: search
              ? previousSearch == search
                ? request?.data?.searchPipelines?.number
                : 0
              : -1,
            previousSearch: search
          }
        };
      } catch (e) {
        // console.log(e);
      }
    },
    onChangePipeline: async optionPipline => {
      try {
        form.setFieldValue('pipeline', optionPipline);

        form.setFieldValue('configuration', null);

        form.setFieldValue('version', { label: '', value: '' });

        form.setFieldValue('replicas', 1);

        form.setFieldValue('projectName', '');

        setConfigurationLastDeploy(null);

        setConfigurations([]);

        setPipelineInstences([]);

        const current = await apiRuntime.pipelineVersions({
          realm: props?.realm,
          environment: props?.environment,
          id: optionPipline?.value
        });

        setMultiInstances({
          replicaName: current?.parameterizedReplica
        });

        form.setFieldValue('isReplicas', !!current?.parameterizedReplica);
        let pipelineLastVersion = maxBy(
          filter(current?.versionMinor, item => !item.disabled),
          item => item.versionMinor
        );

        if (pipelineRedeploy) {
          pipelineLastVersion = pipelineRedeploy;
        }

        validatePipeline({
          pipelineId: pipelineLastVersion?.id,
          environment: props.environment
        });

        await addConfigurations({
          pipelineVersionId: pipelineLastVersion?.id
        });

        const { project } = await api.pipelineProjectName({
          id: current.id,
          realm: props?.realm
        });

        setProjectId(project?.id);
        form.setFieldValue('projectName', project?.name);

        form.setFieldValue('version', {
          value: pipelineLastVersion?.id,
          label: `${pipelineLastVersion?.versionMajor}.${pipelineLastVersion?.versionMinor}`
        });

        if (props.showPromote) {
          setPipeline({
            ...props.deploymentSelected.pipeline,
            triggerSpec: current.triggerSpec
          });

          setSliderProps({
            max: props.deploymentSelected.activeConfiguration?.consumers
          });
          form.setFieldValue(
            'replicas',
            props.deploymentSelected.activeConfiguration?.replicaCount
          );

          form.setFieldValue(
            'configuration',
            normalizeSize(props.deploymentSelected.activeConfiguration?.name)
          );
          form.setFieldValue(
            'consumer',
            props.deploymentSelected.activeConfiguration?.actualConsumers
          );

          form.setFieldValue('environment', {
            value: props.environment,
            label: props.environment
          });

          form.setFieldValue('deletePipe', false);

          setPipeline(current);
          return;
        }

        setConfigAndReplicas({
          pipelineName: pipelineLastVersion?.name,
          versionMajor: pipelineLastVersion?.versionMajor
        });

        setPipeline(current);
      } catch (e) {
        setPipeline(null);
      }
    },
    onChangePipelineVersion: async (optionPipelineVersion, setFieldValue) => {
      form.setFieldValue('version', optionPipelineVersion);

      setConfigurationLastDeploy(null);

      if (!props.showPromote) {
        form.setFieldValue('configuration', null);
      }

      form.setFieldValue(
        'replicas',
        props.deploymentSelected?.activeConfiguration?.replicaCount || 1
      );

      form.setFieldValue('projectName', '');

      setConfigurations([]);

      setValidationPipeline([]);

      const pipelineLastVersion = find(
        pipeline?.versionMinor,
        item => item.id === optionPipelineVersion.value
      );

      form.setFieldValue(
        'isReplicas',
        !!pipelineLastVersion?.parameterizedReplica
      );

      addConfigurations({
        pipelineVersionId: optionPipelineVersion.value
      });
      if (pipelineLastVersion?.id) {
        await validatePipeline({
          pipelineId: pipelineLastVersion?.id,
          environment: props.environment
        });
      }
      setMultiInstances({
        replicaName: pipelineLastVersion?.parameterizedReplica
      });
      if (pipelineLastVersion?.name && !props.showPromote) {
        setConfigAndReplicas({
          pipelineName: pipelineLastVersion?.name,
          versionMajor: pipelineLastVersion?.versionMajor
        });
      }

      const { project } = await api.pipelineProjectName({
        id: pipeline.id,
        realm: props?.realm,
        pipelineMajorVersion: parseInt(optionPipelineVersion.label)
      });

      form.setFieldValue('projectName', project?.name);
    },
    onChangeConfiguration: (
      event,
      { pipelineId, replicas, value, replicasDeploy },
      setFieldValue
    ) => {
      form.setFieldValue('configuration', value);
      const config = find(
        configurations,
        item => normalizeSize(item.name) === value
      );

      checkLicense({
        size: config?.name,
        pipelineId,
        replicas,
        replicaInstanceName: replicasDeploy.value
      });

      setSliderProps({
        max: config.consumers
      });

      form.setFieldValue('consumer', config.actualConsumers);
    },
    onChangeConsumer: async (event, value) => {
      form.setFieldValue('consumer', value);
    },
    onChangeReplicasDeploy: async ({
      pipelineId,
      configurationId,
      replicas,
      replicasDeploy
    }) => {
      form.setFieldValue('replicasDeploy', replicasDeploy);
      form.setFieldValue('configuration', null);

      const pipelineLastVersion = maxBy(
        filter(pipeline?.versionMinor, item => !item.disabled),
        item => item?.versionMinor
      );
      await addConfigurations({
        pipelineVersionId: pipelineLastVersion?.id
      });

      const config = find(
        configurations,
        item => normalizeSize(item.name) === configurationId
      );

      checkLicense({
        pipelineId,
        size: config?.name,
        replicas,
        replicaInstanceName: replicasDeploy.value
      });

      setConfigAndReplicas({
        pipelineName: pipelineLastVersion?.name,
        versionMajor: pipelineLastVersion?.versionMajor,
        replicaName: replicasDeploy.value
      });
    },
    onChangeReplicas: ({
      pipelineId,
      configurationId,
      replicasDeploy,
      value
    }) => {
      form.setFieldValue('replicas', value);
      const config = find(
        configurations,
        item => normalizeSize(item.name) === configurationId
      );
      checkLicense({
        pipelineId,
        size: config?.name,
        replicas: value,
        replicaInstanceName: replicasDeploy.value
      });
    },
    onChangeDeletePipe: value => {
      form.setFieldValue('deletePipe', value);
    },
    onChangeEnvironment: async ({ newEnv, replicas, size }) => {
      const licenseStatus = await apiLicenseStatus.fetch({
        environment: newEnv.value,
        realm: props.realm,
        replicas,
        pipelineId: pipeline.id,
        size
      });
      form.setFieldValue('environment', newEnv);
      setPipeline({
        ...pipeline,
        licenseStatus
      });
    }
  };

  useEffect(() => {
    if (!deployment) return;
    const initial = async () => {
      await actions.onChangePipeline(createObjectOption(deployment));
      await actions.onChangePipelineVersion(
        createObjectPipelineVersion(deployment?.pipeline)
      );
    };

    initial();
    return () => {
      dispatch.runtime.resetDeployment();
    };
  }, [deployment]);

  useEffect(() => {
    async function getPipelineEngine() {
      const { data } = await apiRuntime.deploymentPipelineEngineConfiguration({
        realm: props.realm,
        environment: props.environment
      });

      form.setFieldValue(
        'isPipelineEngine',
        data?.deploymentPipelineEngineConfiguration
      );
    }

    getPipelineEngine();
  }, []);

  useEffect(() => {
    const setPipeline = async () => {
      const pipelineValue = {
        label: pipelineRedeploy.name,
        value: pipelineRedeploy.id
      };
      form.setFieldValue('isPipelineEngine', pipelineRedeploy.isPipelineEngine);
      await actions.onChangePipeline(pipelineValue);
    };

    if (pipelineRedeploy) {
      setPipeline();
    }
  }, [pipelineRedeploy]);

  const versionMinor = map(pipeline?.versionMinor, item => ({
    label: `${item?.versionMajor}.${item?.versionMinor}`,
    value: item?.id
  }));
  const replicasDeploy = map(pipelineInstances, item => ({
    label: item.name,
    value: item.name
  }));
  const trigger = find(
    props.triggers,
    t =>
      t.jsonExample.name ===
        configurationLastDeploy?.pipeline?.triggerSpec?.name &&
      t.jsonExample.type ===
        configurationLastDeploy?.pipeline?.triggerSpec?.type
  );
  const pipelineValidationError = useMemo(
    () =>
      validationPipeline?.filter?.(a => {
        const errors = a?.details.filter(detail => detail?.level == 'ERROR');
        return errors.length;
      }),
    [validationPipeline]
  );

  const closeDialog = () => {
    setShowConfirmDialog(false);
  };

  const confirmDeploy = value => {
    setShowConfirmDialog(false);

    props.find({
      realm: props.realm,
      environment: value.environment,
      search: {
        projectId: value.projectId,
        projectName: value?.projectName
      }
    });
    props.onCloseClick();

    if (isNewRoutesParams) {
      setSearchParams({
        pipelineName: searchParams.get('pipelineName'),
        projectId: value.projectId
      });
    }
  };

  return {
    ...props,
    ...actions,
    versionMinor,
    configurations,
    sliderProps,
    validationPipeline,
    validationLicense,
    configurationLastDeploy,
    replicasDeploy,
    trigger,
    pipelineValidationError,
    licenses: pipeline?.licenseTotal,
    form,
    deployment,
    openPromote,
    closePromote,
    newDeployment,
    pipelineToPromoteConfirm,
    loadingPromote,
    texts: texts[deployType],
    showConfirmDialog,
    closeDialog,
    newDeploy,
    confirmDeploy
  };
});

export default enhancer;
